import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { TextField } from '@ui/inputs';
import { Chip, Consequence, MicroLabel, Row } from '@features/scoreSpec/styles';
import { runVariablesFor } from '@features/scoreSpec/specSchema';
import { errorsFor } from './validate';

// The field the whole form exists for.
//
// The variable rail is per DOCUMENTS KIND, because that is how the engine builds the namespace —
// four of the seven names a `triggering_document` spec gets do not exist for the other three
// kinds. The form never changes the documents kind, so the set resolves once, when the source is
// picked, and cannot go stale underneath the prompt.
//
// Chips insert at the caret rather than appending: a prompt is edited in the middle far more often
// than at the end, and an insert that lands somewhere else is worse than no insert at all.

const FIELD_ID = 'duplicate-prompt';

const VarChip = styled(Chip)`
  cursor: pointer;
  background: transparent;
  font-family: monospace;

  &:hover {
    color: ${({ theme }) => theme.themeColors.text};
    border-color: ${({ theme }) => theme.themeColors.text};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }
`;

export const PromptField = ({ value, onChange, errors, documentsKind }) => {
  const messages = errorsFor(errors, 'prompt');
  const variables = runVariablesFor(documentsKind);

  // The field is looked up by id rather than held in a ref: TextField is not a forwardRef, and an
  // id is already required for its label association, so there is nothing extra to keep in sync.
  const insert = (variable) => {
    const el = document.getElementById(FIELD_ID);
    const token = `{{ ${variable} }}`;
    if (!el) {
      onChange(`${value}${token}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${token}${value.slice(end)}`);
    // The value lands on the next render, so the caret has to be restored after it.
    window.requestAnimationFrame(() => {
      const caret = start + token.length;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  return (
    <div>
      <TextField
        id={FIELD_ID}
        label="Prompt"
        multiline
        mono
        rows={16}
        value={value}
        onChange={onChange}
        invalid={messages.length > 0}
        hint={`${value.length.toLocaleString()} characters`}
      />

      {variables && (
        <Container top={16}>
          <MicroLabel>{`Variables a ${documentsKind} run provides`}</MicroLabel>
          <Container top={8}>
            <Row>
              {variables.map((variable) => (
                <VarChip
                  key={variable}
                  as="button"
                  type="button"
                  onClick={() => insert(variable)}
                  aria-label={`Insert {{ ${variable} }} at the cursor`}
                >
                  {`{{ ${variable} }}`}
                </VarChip>
              ))}
            </Row>
          </Container>
        </Container>
      )}

      {!variables && documentsKind && (
        <Container top={16}>
          <Body7
            isLowContrast
          >{`This client does not know the namespace for documents.${documentsKind}, so template variables are not checked here.`}</Body7>
        </Container>
      )}

      {messages.map((message) => (
        <Container top={8} key={message}>
          <Consequence $tone="bad">{message}</Consequence>
        </Container>
      ))}
    </div>
  );
};
